import { Spacing } from '@persona-id/design-tokens';
import { partition, snakeCase } from 'lodash/fp';
import { Controller, UseFormReturn } from 'react-hook-form';
import styled from 'styled-components';

import { FieldWrapper } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/shared';
import { InsightPanelForm } from 'dashboard/components/InsightPanel/util';
import { InsightResponseType, InsightType } from 'dashboard/models/document';
import { VerificationTemplateVersionDocument } from 'dashboard/models/verification-template-version';
import Checkbox from 'design-system/components/Checkbox';
import { ErrorText, WarningText } from 'design-system/components/FormControl';
import Input from 'design-system/components/Input';
import Select, { Option } from 'design-system/components/Select';
import Spacer from 'design-system/components/Spacer';
import { Label, Paragraph } from 'design-system/components/Text';
import ToggleGroup, { ToggleGroupItem } from 'design-system/components/ToggleGroup';
import Tooltip from 'design-system/components/Tooltip';
import { FIELD_SCHEMA_TYPE_DISPLAY, FieldSchemaType } from 'lib/fields';

const Section = styled.section`
  padding: ${Spacing[4]};
  border-bottom: 1px solid ${({ theme }) => theme.color.border.primary};
`;

const StyledLabel = styled(Label)`
  margin-bottom: ${Spacing[2]};
  display: inline-block;
`;

// TODO: [Documents] Migrate to PDS component for underline tooltip indicators when available
const TooltipIndicatorWrapper = styled.div`
  width: fit-content;
  text-decoration: underline;
  text-decoration-style: dotted;
`;

const TooltipParagraph = styled(Paragraph)`
  width: 200px;
`;

const FieldErrorText = ({ message }: { message?: string }) =>
  message ? (
    <>
      <Spacer height={0.5} />
      <ErrorText>{message}</ErrorText>
    </>
  ) : null;

const getCompatibleFieldType = (insightType: InsightType, responseType: InsightResponseType) => {
  switch (insightType) {
    case InsightType.String:
      return FieldSchemaType.String;
    case InsightType.MultiSelect:
      switch (responseType) {
        case InsightResponseType.Single:
          return FieldSchemaType.String;
        case InsightResponseType.Multiple:
          return FieldSchemaType.Array;
        default:
          return null;
      }
    default:
      return null;
  }
};

interface InsightPanelAdvancedProps {
  formReturn: UseFormReturn<InsightPanelForm, object>;
  vtmplVersion: VerificationTemplateVersionDocument;
}

const InsightPanelAdvanced = ({ formReturn, vtmplVersion }: InsightPanelAdvancedProps) => {
  const { register, formState, watch, resetField, clearErrors, setValue, getValues, control } =
    formReturn;
  const { errors } = formState;

  const insightType = watch('insightType');
  const responseType = watch('responseType');
  const saveToDocumentField = watch('saveToDocumentField');
  const isNewDocumentField = watch('isNewDocumentField');
  const documentFieldKey = watch('documentFieldKey');
  const compatibleDocumentFieldType = getCompatibleFieldType(insightType, responseType);

  const documentFields = vtmplVersion.attributes['field-schemas'] || [];
  const [compatibleDocumentFields, incompatibleDocumentFields] = partition(
    (documentField) => documentField.type === compatibleDocumentFieldType,
    documentFields,
  );

  const fieldWriteBacks = vtmplVersion.attributes['field-write-backs'] || [];

  const sourcesWritingToField =
    fieldWriteBacks
      .find((fieldWriteBack) => fieldWriteBack['field-name'] === documentFieldKey)
      // filter out the current source to not conflict with itself
      ?.options.filter((option) => option['source-key'] !== formState.defaultValues?.insightKey)
      .map((option) => ({
        source_key: option['source-key'],
        source_type: option['source-type'],
      })) || [];

  return (
    <Section>
      <Paragraph weight='bold' color='primary'>
        Advanced
      </Paragraph>
      <Spacer height={1.5} />
      <Controller
        name='saveToDocumentField'
        control={control}
        render={({ field }) => (
          <Checkbox
            {...field}
            label='Save analysis output to a Document Field'
            checked={field.value}
            onChange={(e) => {
              field.onChange(e);

              const insightKey = getValues('insightKey');
              if (field.value) {
                resetField('isNewDocumentField');
                resetField('documentFieldName');
                resetField('documentFieldKey');
              } else if (compatibleDocumentFields.map(({ key }) => key).includes(insightKey)) {
                setValue('documentFieldKey', insightKey);
              }
            }}
          />
        )}
      />
      {saveToDocumentField && (
        <>
          <Spacer height={1.5} />
          <Controller
            name='isNewDocumentField'
            control={control}
            render={({ field }) => (
              <ToggleGroup
                value={field.value.toString()}
                onValueChange={(e) => {
                  field.onChange(e === 'true');

                  const insightName = getValues('insightName');
                  const insightKey = getValues('insightKey');
                  const compatibleExists = compatibleDocumentFields.some(
                    ({ key }) => key === insightKey,
                  );
                  if (field.value) {
                    setValue('documentFieldName', '');
                    setValue('documentFieldKey', compatibleExists ? insightKey : '');
                  } else {
                    setValue('documentFieldName', compatibleExists ? '' : insightName);
                    setValue('documentFieldKey', compatibleExists ? '' : insightKey);
                  }
                  clearErrors('documentFieldName');
                  clearErrors('documentFieldKey');
                }}
                type='single'
                fullWidth
              >
                <ToggleGroupItem value='false'>Existing field</ToggleGroupItem>
                <ToggleGroupItem value='true'>New field</ToggleGroupItem>
              </ToggleGroup>
            )}
          />
          {isNewDocumentField ? (
            <>
              <Spacer height={1.5} />
              <StyledLabel>New document field label</StyledLabel>
              <Controller
                name='documentFieldName'
                control={control}
                render={({ field }) => (
                  <FieldWrapper>
                    <Input
                      {...field}
                      placeholder='Enter new document field label'
                      hasError={!!errors?.documentFieldName}
                      onChange={(e) => {
                        field.onChange(e);
                        setValue('documentFieldKey', snakeCase(e.target.value));
                      }}
                    />
                  </FieldWrapper>
                )}
              />
              <FieldErrorText message={errors?.documentFieldName?.message} />
              <Spacer height={1.5} />
              <StyledLabel>Document field key</StyledLabel>
              <FieldWrapper>
                <Input
                  {...register('documentFieldKey')}
                  disabled
                  hasError={!!errors?.documentFieldKey}
                />
              </FieldWrapper>
              <FieldErrorText message={errors?.documentFieldKey?.message} />
              {compatibleDocumentFieldType && (
                <>
                  <Spacer height={1.5} />
                  <Tooltip
                    side='right'
                    trigger={
                      <StyledLabel>
                        <TooltipIndicatorWrapper>Type</TooltipIndicatorWrapper>
                      </StyledLabel>
                    }
                  >
                    <TooltipParagraph>
                      The document field type is determined by the prompt template and response
                      type.
                    </TooltipParagraph>
                  </Tooltip>
                  <FieldWrapper>
                    <Input
                      value={FIELD_SCHEMA_TYPE_DISPLAY[compatibleDocumentFieldType]}
                      disabled
                    />
                  </FieldWrapper>
                </>
              )}
            </>
          ) : (
            <>
              <Spacer height={1.5} />
              <Tooltip
                side='right'
                trigger={
                  <StyledLabel>
                    <TooltipIndicatorWrapper>Document fields</TooltipIndicatorWrapper>
                  </StyledLabel>
                }
              >
                <TooltipParagraph>
                  Compatible document field types are determined by the prompt template and response
                  type. Incompatible fields are disabled.
                </TooltipParagraph>
              </Tooltip>
              <Controller
                name='documentFieldKey'
                control={control}
                render={({ field }) => (
                  <FieldWrapper>
                    <Select
                      {...field}
                      placeholder={
                        documentFields.length > 0
                          ? 'Select document field'
                          : 'No existing document fields'
                      }
                      hasError={!!errors?.documentFieldKey}
                      hasWarning={sourcesWritingToField.length > 0}
                      disabled={documentFields.length === 0}
                    >
                      {compatibleDocumentFields.map((documentField) => (
                        <Option
                          key={documentField.key}
                          value={documentField.key}
                          subtitle={`${FIELD_SCHEMA_TYPE_DISPLAY[documentField.type]}: ${documentField.key}`}
                        >
                          {documentField.label}
                        </Option>
                      ))}
                      {incompatibleDocumentFields.map((documentField) => (
                        <Option
                          key={documentField.key}
                          value={documentField.key}
                          subtitle={`${FIELD_SCHEMA_TYPE_DISPLAY[documentField.type]}: ${documentField.key}`}
                          disabled
                        >
                          {documentField.label}
                        </Option>
                      ))}
                    </Select>
                  </FieldWrapper>
                )}
              />
              <FieldErrorText message={errors?.documentFieldKey?.message} />
              {sourcesWritingToField.map((source) => (
                <>
                  <Spacer height={1.5} />
                  <WarningText>
                    This field is already being written to by {source.source_type}{' '}
                    {source.source_key}
                  </WarningText>
                </>
              ))}
            </>
          )}
        </>
      )}
    </Section>
  );
};

export default InsightPanelAdvanced;
